import { getLocationQualifiers } from "@atlas-int/data";
import type { CountryFeature } from "./types";

// Upper/lower bounds of color range of map:
export const colorRangeStart = "#B1E0BB";
export const colorRangeEnd = "#0A4486";

// For example, if
// `spectrumStartPercent` is 1/2 and
// the `nice`-d domain starts at 10^3 and ends at 10^7 then the color spectrum
// starts at 10^5 because 5 is midway between 3 and 7:
export const spectrumStartPercent = 0.5;
export const spectrumEndPercent = 0.9;

export const getOrdersOfMagnitudeDifference = (
  domainStart: number,
  domainEnd: number,
) => {
  // Note: the `log10` operations will produce integers because `domainStart`
  // and `domainEnd` will be powers of ten because they have been `nice`-d by `d3`:
  const domainStartOrderOfMagnitude = Math.log10(domainStart);
  const domainEndOrderOfMagnitude = Math.log10(domainEnd);
  return domainEndOrderOfMagnitude - domainStartOrderOfMagnitude;
};

export const defaultMapFillColor = "white";
export const defaultMapStrokeColor = "#8AABAB";
export const primaryCountryStrokeColor = "#FFFFFF";
export const primaryCountryColor = "#e8c258";

export const getReferenceLocation = ({
  exporter,
  importer,
}: {
  exporter: string | undefined;
  importer: string | undefined;
}) => {
  const {
    exporterIsUndefined,
    exporterIsWorld,
    importerIsUndefined,
    importerIsWorld,
  } = getLocationQualifiers({ exporter, importer });
  let referenceLocation;

  if (!exporterIsUndefined && !importerIsUndefined) {
    if (exporterIsWorld) {
      referenceLocation = importer;
    } else if (importerIsWorld) {
      referenceLocation = exporter;
    } else {
      referenceLocation = exporter;
    }
  } else {
    if (exporterIsUndefined) {
      referenceLocation = importer;
    } else if (importerIsUndefined) {
      referenceLocation = exporter;
    } else {
      referenceLocation = exporter;
    }
  }

  return referenceLocation;
};

/** Bounding-box area threshold (degrees²) below which a selected country is
 *  considered too small to interact with reliably and gets an auto-pinned tooltip.
 *  Kuwait ~ 2.97, Belgium ~ 7.40 */
export const SMALL_COUNTRY_AREA_THRESHOLD = 6.5;

/** Approximate tooltip height used for vertical clamping. Intentionally a fixed
 *  estimate — avoids a DOM read on every map move event. */
export const TOOLTIP_HEIGHT_ESTIMATE = 150;

/**
 * Returns the outer-ring coordinates of the largest individual polygon in a feature.
 * For `Polygon`, returns the single outer ring.
 * For `MultiPolygon`, returns the ring with the largest bounding-box area.
 *
 * Using the largest polygon (rather than all coords flattened) correctly handles
 * archipelagos (Bahamas, Solomon Islands etc.) — each island is small even though
 * the combined bounding box of all islands is large.
 */
export function getRepresentativePolygonCoords(
  feature: CountryFeature,
): number[][] | null {
  const { geometry } = feature;
  if (!geometry) return null;

  if (geometry.type === "Polygon") {
    return (geometry as { type: "Polygon"; coordinates: number[][][] })
      .coordinates[0];
  }

  if (geometry.type === "MultiPolygon") {
    const rings = (
      geometry as { type: "MultiPolygon"; coordinates: number[][][][] }
    ).coordinates.map((poly) => poly[0]);

    let largest: number[][] = rings[0];
    let largestArea = -Infinity;
    for (const ring of rings) {
      const lons = ring.map((c) => c[0]);
      const lats = ring.map((c) => c[1]);
      const area =
        (Math.max(...lons) - Math.min(...lons)) *
        (Math.max(...lats) - Math.min(...lats));
      if (area > largestArea) {
        largestArea = area;
        largest = ring;
      }
    }
    return largest;
  }

  return null;
}

/** Returns a stable geographic anchor for the pinned tooltip. */
export function getPinnedTooltipAnchorCoords(
  feature: CountryFeature,
): [number, number] | null {
  const coords = getRepresentativePolygonCoords(feature);
  if (!coords) return null;

  const lons = coords.map((coord) => coord[0]);
  const lats = coords.map((coord) => coord[1]);

  return [
    lons.reduce((total, value) => total + value, 0) / lons.length,
    Math.max(...lats),
  ];
}

/** Returns the bounding-box area of the largest individual polygon in the feature. */
export function getBoundingBoxArea(feature: CountryFeature): number {
  const coords = getRepresentativePolygonCoords(feature);
  if (!coords) return Infinity;
  const lons = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return (
    (Math.max(...lons) - Math.min(...lons)) *
    (Math.max(...lats) - Math.min(...lats))
  );
}
